import { DocumentLead } from '@/api/generatedApi';
import { useGetDocumentPreview } from '@/hooks/api/part-finder-reports/useGetDocumentPreview';
import { useObjectUrl } from '@/hooks/useObjectUrl';
import { ChevronLeft, ChevronRight, Close } from '@mui/icons-material';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import { useEffect } from 'react';
import { DocumentActionLinks } from './DocumentActionLinks';

export interface PreviewableDocument {
  lead: DocumentLead;
  index: number;
}

interface DocumentPreviewLightboxProperties {
  reportId: string;
  documents: PreviewableDocument[];
  activePosition: number;
  onNavigate: (position: number) => void;
  onClose: () => void;
}

export const DocumentPreviewLightbox = ({
  reportId,
  documents,
  activePosition,
  onNavigate,
  onClose,
}: DocumentPreviewLightboxProperties) => {
  const { lead, index } = documents[activePosition];
  const { preview } = useGetDocumentPreview({ reportId, index, size: 'full' });
  const previewUrl = useObjectUrl(preview);
  const hasMultiple = documents.length > 1;

  const goTo = (offset: number) => onNavigate((activePosition + offset + documents.length) % documents.length);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      const step = documents.length;
      if (event.key === 'ArrowLeft') {
        onNavigate((activePosition - 1 + step) % step);
      } else if (event.key === 'ArrowRight') {
        onNavigate((activePosition + 1) % step);
      }
    };
    globalThis.addEventListener('keydown', handleKey);
    return () => globalThis.removeEventListener('keydown', handleKey);
  }, [activePosition, documents.length, onNavigate]);

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      sx={{ '& .MuiDialog-paper': { height: 'calc(100% - 64px)' } }}
    >
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '8px',
          p: 2,
          height: '100%',
        }}
      >
        <IconButton aria-label='Close' onClick={onClose} sx={{ position: 'absolute', top: 8, right: 8 }}>
          <Close />
        </IconButton>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', width: '100%', flex: 1, minHeight: 0 }}>
          {hasMultiple && (
            <IconButton aria-label='Previous document' onClick={() => goTo(-1)}>
              <ChevronLeft />
            </IconButton>
          )}
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minHeight: 0, height: '100%' }}>
            {previewUrl && (
              <Box
                component='img'
                src={previewUrl}
                alt={`First page of ${lead.title}`}
                sx={{
                  maxWidth: '100%',
                  height: '100%',
                  objectFit: 'contain',
                  border: '1px solid',
                  borderColor: 'divider',
                  boxSizing: 'border-box',
                }}
              />
            )}
          </Box>
          {hasMultiple && (
            <IconButton aria-label='Next document' onClick={() => goTo(1)}>
              <ChevronRight />
            </IconButton>
          )}
        </Box>
        <Typography variant='caption' sx={{ color: 'text.secondary' }}>
          {hasMultiple ? `${lead.title} · ${activePosition + 1} / ${documents.length}` : lead.title}
        </Typography>
        <DocumentActionLinks lead={lead} reportId={reportId} index={index} />
      </Box>
    </Dialog>
  );
};
